interface Product {
  id: string | number
  name: string
  displayName?: string
}

interface CompanyType {
  id: string | number
  title: string
  name?: string
}

interface DiscountingData {
  id: string | number
  product?: {
    name: string
    displayName?: string
  }
  company_type?: {
    title: string
    name?: string
  }
  minimum_quantity?: string | number
  minimum_order_quantity?: string | number
  discount_percentage?: string | number
}

interface SelectOption {
  id: string | number
  title: string
}

interface BaseFormInput {
  name: string
  prompt: string
  placeholder: string
  required: boolean
  initialValue: string
  readOnly?: boolean
}

interface TextFormInput extends BaseFormInput {
  inputType: "text"
  rules: { required: boolean }
  isFullWidth: boolean
}

interface SelectFormInput extends BaseFormInput {
  inputType: "selectInput"
  rules: {
    required: {
      expectedValue: boolean
      errorMessage: string
    }
  }
  fullWidth: boolean
  options: SelectOption[]
  loadingState: boolean
  returnId: boolean
}

type FormInput = TextFormInput | SelectFormInput

export const TableKeys = ["product", "buyerType", "minimumQuantity", "discount"]

export const TableHeaders = ["Product", "Buyer Type", "Minimum Order Quantity", "Discount"]

export const TableData = (discountingData: DiscountingData[]) => {
  if (!discountingData || !Array.isArray(discountingData)) {
    return []
  }

  return discountingData.map((discounting: DiscountingData) => {
    return {
      id: discounting.id,
      product: {
        value: discounting.product?.name || "N/A",
        type: "string",
      },
      buyerType: {
        value: discounting.company_type?.title || "N/A",
        type: "string",
      },
      minimumQuantity: {
        value: discounting.minimum_quantity || "N/A",
        type: "string",
      },
      discount: {
        value: discounting.discount_percentage ? `${parseFloat(String(discounting.discount_percentage))}%` : "N/A",
        type: "string",
      },
    }
  })
}

export const getFormInputs = (
  discounting?: DiscountingData | null,
  products?: Product[],
  productsLoading?: boolean,
  companyTypes?: CompanyType[],
  companyTypesLoading?: boolean,
): FormInput[] => {
  const productOptions: SelectOption[] =
    products?.map((product: Product) => ({
      id: product.id,
      title: product.displayName || product.name,
    })) || []

  const companyTypeOptions: SelectOption[] =
    companyTypes
      ?.filter((type: CompanyType) => {
        const name = (type.title || type.name || "").toLowerCase()
        return !name.includes("manufacturer")
      })
      .map((type: CompanyType) => ({
        id: type.id,
        title: type.title || type.name || "",
      })) || []

  if (discounting) {
    return [
      {
        inputType: "selectInput",
        name: "company_type_id",
        prompt: "Buyer Type",
        placeholder: "Select buyer type",
        fullWidth: false,
        rules: {
          required: {
            expectedValue: true,
            errorMessage: "Buyer Type is required",
          },
        },
        initialValue: discounting.company_type?.title || "",
        options: companyTypeOptions,
        loadingState: companyTypesLoading || false,
        returnId: true,
        required: false,
        readOnly: true,
      },
      {
        inputType: "selectInput",
        name: "product_id",
        prompt: "Product Name",
        placeholder: "Select product",
        fullWidth: false,
        rules: {
          required: {
            expectedValue: true,
            errorMessage: "Product Name is required",
          },
        },
        initialValue: discounting.product?.name || "",
        options: productOptions,
        loadingState: productsLoading || false,
        returnId: true,
        required: false,
        readOnly: true,
      },
      {
        inputType: "text",
        name: "minimum_order_quantity",
        prompt: "Minimum Order Quantity",
        placeholder: "Enter minimum order quantity",
        rules: { required: true },
        isFullWidth: true,
        required: true,
        initialValue: String(discounting?.minimum_quantity || discounting?.minimum_order_quantity || ""),
        readOnly: false,
      },
      {
        inputType: "text",
        name: "discount_percentage",
        prompt: "Discount Percentage (%)",
        placeholder: "Enter discount percentage (0-100)",
        rules: { required: true },
        isFullWidth: true,
        required: true,
        initialValue: String(discounting?.discount_percentage || ""),
        readOnly: false,
      },
    ]
  }

  return [
    {
      inputType: "selectInput",
      name: "company_type_id",
      prompt: "Buyer Type",
      placeholder: "Select buyer type",
      fullWidth: false,
      rules: {
        required: {
          expectedValue: true,
          errorMessage: "Buyer Type is required",
        },
      },
      initialValue: "",
      options: companyTypeOptions,
      loadingState: companyTypesLoading || false,
      returnId: true,
      required: true,
    },
    {
      inputType: "selectInput",
      name: "product_id",
      prompt: "Product Name",
      placeholder: "Select product",
      fullWidth: false,
      rules: {
        required: {
          expectedValue: true,
          errorMessage: "Product Name is required",
        },
      },
      initialValue: "",
      options: productOptions,
      loadingState: productsLoading || false,
      returnId: true,
      required: true,
    },
    {
      inputType: "text",
      name: "minimum_order_quantity",
      prompt: "Minimum Order Quantity",
      placeholder: "Enter minimum order quantity",
      rules: { required: true },
      isFullWidth: true,
      required: true,
      initialValue: "",
    },
    {
      inputType: "text",
      name: "discount_percentage",
      prompt: "Discount Percentage (%)",
      placeholder: "Enter discount percentage (0-100)",
      rules: { required: true },
      isFullWidth: true,
      required: true,
      initialValue: "",
    },
  ]
}
